/* ------------------------------------*\
    #OVERLAY SCRIPT
\*------------------------------------ */

import Component from '../module/component';

/**
 * The component is a generic UI abstraction for the dialog pattern.
 * @class Overlay
 * @extends Component
 */
export default class Dialog extends Component {

    static componentName = 'dialog';

    // "abstract" component, not for direct use
    static componentSelector = '';

    static defaultOptions = {
        triggerSelector: '.js-dialog-trigger',
        closeSelector: '.js-dialog-close',
        openEventName: 'bronson_dialog_open',
        closeEventName: 'bronson_dialog_close',
        cancelEventName: 'bronson_dialog_cancel',
        lightDismiss: false,
        modal: true,
    };

    // https://github.com/material-components/material-web/blob/main/dialog/internal/dialog.ts#L563
    // button is excluded on purpose as it is acceptable to close on ESC when a button is focused,
    // it is even intended when the only focusable element is a button (e.g. close button).
    static isFocusable(element) {
        const knownFocusableElements =
             ':is(input,select,textarea,object,:is(a,area)[href],[tabindex],[contenteditable=true])';
        const notDisabled = ':not(:disabled,[disabled])';
        const notNegativeTabIndex = ':not([tabindex^="-"])';

        return element.matches(knownFocusableElements + notDisabled + notNegativeTabIndex);
    }

    constructor() {
        super(...arguments);

        // Safeguard if `componentSelector` does not include `dialog`
        if (this.element.nodeName !== 'DIALOG') {
            // eslint-disable-next-line no-console
            console.error('Component`s root element needs to be <dialog> element');
        }

        this.identifier = this.element.getAttribute('id');

        /**
         * Setup triggers per instance to make prototypical inheritance more robust
         * and avoid overwriting super-class static properties.
         */
        this.setupTriggers();

        // Get all closers that are inside the component.
        this.$$closers = this.query(this.options.closeSelector);

        // Register to open event
        this.$$element.on(this.options.openEventName, () => {
            this.open();
        });

        // Register to close event
        this.$$element.on(this.options.closeEventName, () => {
            this.close();
        });

        // Register to `cancel` event
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/cancel_event
        this.$$element.on(this.options.cancelEventName, () => {
        });

        // Register closers
        this.$$closers.on('click', (event) => {
            event.preventDefault();
            this.triggerClose();
        });

        if (this.options.lightDismiss) {
            // https://codepen.io/argyleink/pen/GRyxpgr
            this.element.addEventListener('click', (event) => {
                if (event.target.nodeName === 'DIALOG') {
                    this.triggerClose();
                }
            });
        }

        this.element.addEventListener('keydown', (event) => {
            // Override dialog's default close on ESC
            if (event.code === 'Escape') {
                event.preventDefault();
            }

            if (event.code === 'Escape' && !Dialog.isFocusable(event.target)) {
                this.triggerClose(true);
            }
        });
    }

    /**
     * Setup triggers to open reference dialog
     */
    setupTriggers() {
        this.$triggers = document.querySelectorAll(`[data-${this.constructor.componentName}="${this.identifier}"]`);
        this.$triggers?.forEach(($trigger) => {
            $trigger.addEventListener('click', (event) => {
                event.preventDefault();
                // Extract the target’s element identifier from the trigger reference attribute.
                const ref = $trigger.getAttribute(`data-${this.constructor.componentName}`);

                // Query the target element.
                const target = document.querySelector(`#${ref}`);

                // Emit the {this.options.openEventName} for the target.
                if (target) {
                    // Dispatch a custom element to inform the connected dialog to open
                    target.dispatchEvent(new Event(this.options.openEventName));
                }
            });
        });
    }

    /**
     * Dispatch event to close dialog / trigger close
     */
    triggerClose(cancel = false) {
        if (cancel) {
            this.element.dispatchEvent(new Event(this.options.cancelEventName));
        }

        this.element.dispatchEvent(new Event(this.options.closeEventName));
    }

    /**
     * Open the dialog in either modal or non-modal mode.
     *
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/showModal
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/show
     */
    open() {
        if (this.options.modal) {
            this.element.showModal();
        } else {
            this.element.show();
        }
    }

    /**
     * Close the dialog.
     *
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLDialogElement/close_event
     */
    close() {
        this.element.close();
    }

}
